import { Card } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { dashboardLayout } from "../lib/dashboard-layout";
import {
  endOfWeek,
  format,
  parseISO,
  startOfDay,
  startOfWeek,
} from "date-fns";
import { CalendarClock, ListTodo } from "lucide-react";
import { useMemo } from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { CalendarEventDto } from "@/modules/management/calendar/domain/types";
import {
  useCalendarEvents,
  useCalendarLinkedItems,
} from "@/modules/management/calendar/presentation/api/queries";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { DashboardSection } from "./DashboardSection";

function useWeekRange() {
  return useMemo(() => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 0 });
    const end = endOfWeek(now, { weekStartsOn: 0 });
    return {
      startStr: format(start, "yyyy-MM-dd"),
      endStr: format(end, "yyyy-MM-dd"),
    };
  }, []);
}

function formatEventWhen(event: CalendarEventDto): string {
  const start = parseISO(event.startAt);
  const day = format(start, "d MMM");
  const time = format(start, "HH:mm");
  return `${day} · ${time}`;
}

type DueItem = {
  id: string;
  title: string;
  subtitle: string;
  dueDate: string;
};

export function DashboardMyWorkPanel() {
  const { user } = useAuthState();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const { startStr, endStr } = useWeekRange();
  const userId = user?.id ?? "";

  const canCalendar = permissions.hasAny([
    Permissions.mgmt_calendar.read,
    Permissions.mgmt_calendar.team,
  ]);

  const linkedQuery = useCalendarLinkedItems(
    userId && canCalendar
      ? { start: startStr, end: endStr, userId }
      : null,
  );
  const eventsQuery = useCalendarEvents({
    start: startStr,
    end: endStr,
    userId: userId || undefined,
  });

  const dueItems = useMemo((): DueItem[] => {
    const linked = linkedQuery.data;
    if (!linked) return [];

    const tasks: DueItem[] = linked.projectTasks.map((t) => ({
      id: `task-${t.id}`,
      title: t.title,
      subtitle: t.projectName ? `ໂຄງການ: ${t.projectName}` : "Task",
      dueDate: t.dueDate,
    }));
    const actions: DueItem[] = linked.meetingActions.map((a) => ({
      id: `action-${a.id}`,
      title: a.description,
      subtitle: a.meetingTitle ? `ປະຊຸມ: ${a.meetingTitle}` : "ປະເດັນ",
      dueDate: a.dueDate,
    }));

    return [...tasks, ...actions]
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      .slice(0, 6);
  }, [linkedQuery.data]);

  const upcomingEvents = useMemo(() => {
    const events = eventsQuery.data ?? [];
    const today = startOfDay(new Date());
    return [...events]
      .sort((a, b) => a.startAt.localeCompare(b.startAt))
      .filter((e) => parseISO(e.startAt) >= today)
      .slice(0, 6);
  }, [eventsQuery.data]);

  if (!canCalendar || !userId) return null;

  const loading = linkedQuery.isLoading || eventsQuery.isLoading;

  return (
    <DashboardSection
      title="ວຽກຂອງຂ້ອຍ · ອາທິດນີ້"
      action={
        <button
          type="button"
          className="text-primary text-xs hover:underline"
          onClick={() => navigate({ to: "/app/management/calendar" })}
        >
          ເບິ່ງປະຕິທິນ →
        </button>
      }
    >
      <div className={`${dashboardLayout.cardGrid} lg:grid-cols-2`}>
        <Card
          className={`${dashboardLayout.cardChrome} ${dashboardLayout.panelCard}`}
        >
          <div className="flex items-center gap-2">
            <ListTodo className="size-4 text-primary" />
            <span className="font-medium text-sm">Task / ປະເດັນ (due)</span>
          </div>
          {loading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : dueItems.length === 0 ? (
            <p className="text-muted-foreground text-sm">ບໍ່ມີລາຍການໃນອາທິດນີ້</p>
          ) : (
            <ul className={dashboardLayout.innerList}>
              {dueItems.map((item) => (
                <li
                  key={item.id}
                  className={`rounded-md border border-border/60 text-sm ${dashboardLayout.listItemPad}`}
                >
                  <p className="line-clamp-2 font-medium leading-snug">
                    {item.title}
                  </p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    {item.subtitle} · due {item.dueDate}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card
          className={`${dashboardLayout.cardChrome} ${dashboardLayout.panelCard}`}
        >
          <div className="flex items-center gap-2">
            <CalendarClock className="size-4 text-primary" />
            <span className="font-medium text-sm">ກິດຈະກຳປະຕິທິນ</span>
          </div>
          {loading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : upcomingEvents.length === 0 ? (
            <p className="text-muted-foreground text-sm">ບໍ່ມີກິດຈະກຳຖັດໄປ</p>
          ) : (
            <ul className={dashboardLayout.innerList}>
              {upcomingEvents.map((event) => (
                <li
                  key={event.id}
                  className={`flex items-start justify-between gap-2 rounded-md border border-border/60 text-sm ${dashboardLayout.listItemPad}`}
                >
                  <span className="line-clamp-2 font-medium leading-snug">
                    {event.title}
                  </span>
                  <span className="shrink-0 text-muted-foreground text-xs tabular-nums">
                    {formatEventWhen(event)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </DashboardSection>
  );
}
